<template>
  <v-container fluid>
    <div class="text-end">
      <v-btn to="/delivery" outlined color="primary">
        שליחים
        <v-icon>arrow_back</v-icon>
      </v-btn>
    </div>
    <div v-if="coordinates">
      <v-row style="position: relative; z-index: 1000">
        <v-col cols="4">
          <v-select
            v-model="delivery_id"
            :items="drivers"
            label="משאבים"
            outlined
            dense
            item-text="name"
            item-value="id"
            max-width="200"
            background-color="white"
          >
            <template v-slot:item="{ item }">
              <v-list-item-title @click="driverSelect(item)">
                {{ item.name }}
                <span
                  v-if="
                    !item.lat || !item.lng || isNaN(item.lat) || isNaN(item.lng)
                  "
                  class="text-caption text-grey"
                >
                  (لا يوجد موقع)
                </span>
              </v-list-item-title>
            </template>
          </v-select>
        </v-col>
        <v-col cols="6">
          <v-btn
            color="darkGreen"
            class="mb-2 font-weight-bold"
            @click="centerOnAllDrivers"
          >
            מרכז על כל השליחים
          </v-btn>
        </v-col>
      </v-row>
      <div id="map"></div>
    </div>
    <v-alert v-else type="error" class="text-center"> אין מיקום זמין </v-alert>
  </v-container>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import * as Ably from "ably";
const selectedDriverIcon = L.icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/854/854866.png",
  iconSize: [36, 36],
  iconAnchor: [18, 36],
});

export default {
  name: "DriversMap",
  data() {
    return {
      map: null,
      markers: [],
      selectedDriver: null,
      ably: null,
      channel: null,
      delivery_id: null,

      drivers: [],
    };
  },

  methods: {
    driverSelect(driver) {
      if (
        driver &&
        driver.lat &&
        driver.lng &&
        !isNaN(driver.lat) &&
        !isNaN(driver.lng)
      ) {
        this.centerOnDriver(driver);
      }
    },
    initMap() {
      if (this.map) return;

      if (!this.coordinates) return;

      const driversWithCoords = this.drivers.filter(
        (d) => d.lat && d.lng && !isNaN(d.lat) && !isNaN(d.lng)
      );
      let center = { lat: this.coordinates[0], lng: this.coordinates[1] };
      if (driversWithCoords.length > 0) {
        const avgLat =
          driversWithCoords.reduce((sum, d) => sum + d.lat, 0) /
          driversWithCoords.length;
        const avgLng =
          driversWithCoords.reduce((sum, d) => sum + d.lng, 0) /
          driversWithCoords.length;

        // Validate the calculated coordinates
        if (!isNaN(avgLat) && !isNaN(avgLng) && avgLat !== 0 && avgLng !== 0) {
          center = { lat: avgLat, lng: avgLng };
        }
      }
      this.map = L.map("map").setView([center.lat, center.lng], 13);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "© OpenStreetMap",
      }).addTo(this.map);
      this.drawMarkers();
    },
    drawMarkers() {
      this.markers.forEach((m) => this.map.removeLayer(m));
      this.markers = [];

      // Draw all drivers on the map
      this.drivers.forEach((driver) => {
        if (
          driver.lat &&
          driver.lng &&
          !isNaN(driver.lat) &&
          !isNaN(driver.lng)
        ) {
          // Use different icon for selected driver
          const isSelected = this.isSelectedDriver(driver);
          const icon = isSelected
            ? selectedDriverIcon
            : L.icon({
                iconUrl:
                  "https://cdn-icons-png.flaticon.com/512/854/854866.png",
                iconSize: [30, 30],
                iconAnchor: [15, 30],
              });

          // Create driver marker
          const driverMarker = L.marker([driver.lat, driver.lng], {
            icon,
          }).addTo(this.map);

          // Create driver label with different style for selected driver
          const labelStyle = isSelected
            ? "background: #1976d2; color: #fff; font-weight: bold; padding: 4px 12px; border-radius: 8px; border: 2px solid #1565c0; box-shadow: 0 4px 8px rgba(0,0,0,0.3); display: inline-block; white-space: nowrap; font-size: 14px; text-align: center;"
            : "background: #1976d2; color: #fff; font-weight: bold; padding: 2px 10px; border-radius: 6px; border: 1px solid #1565c0; box-shadow: 0 2px 6px rgba(0,0,0,0.15); display: inline-block; white-space: nowrap; text-align: center;";

          const driverLabel = L.marker([driver.lat, driver.lng], {
            icon: L.divIcon({
              className: "driver-label",
              html: `
              <span style="${labelStyle}">
                ${driver.name}
                <br>
                ${driver.phone}
                </span>
              `,
              iconSize: isSelected ? [100, 32] : [80, 24],
              iconAnchor: isSelected ? [50, -20] : [40, -16],
            }),
          }).addTo(this.map);

          this.markers.push(driverMarker);
          this.markers.push(driverLabel);
        }
      });
    },
    updateMap() {
      if (this.map) this.drawMarkers();
    },

    getDistance(lat1, lng1, lat2, lng2) {
      const toRad = (x) => (x * Math.PI) / 180;
      const R = 6371;
      const dLat = toRad(lat2 - lat1);
      const dLng = toRad(lng2 - lng1);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) *
          Math.cos(toRad(lat2)) *
          Math.sin(dLng / 2) *
          Math.sin(dLng / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    },
    centerOnAllDrivers() {
      const driversWithCoords = this.drivers.filter(
        (d) => d.lat && d.lng && !isNaN(d.lat) && !isNaN(d.lng)
      );

      if (driversWithCoords.length > 0 && this.map) {
        try {
          // Calculate bounds manually (most reliable method)
          const bounds = this.calculateBounds(driversWithCoords);
          if (bounds) {
            this.map.fitBounds(bounds, {
              padding: [50, 50],
              maxZoom: 15,
            });
          } else {
            // Fallback to first driver
            this.map.setView(
              [driversWithCoords[0].lat, driversWithCoords[0].lng],
              13
            );
          }
        } catch (error) {
          // Final fallback to default location
          this.map.setView([this.coordinates[0], this.coordinates[1]], 13);
        }
      } else if (this.map) {
        // If no drivers with valid coordinates, center on default location
        this.map.setView([this.coordinates[0], this.coordinates[1]], 13);
      }
    },

    calculateBounds(drivers) {
      if (!drivers || drivers.length == 0) return null;

      try {
        const lats = drivers.map((d) => d.lat);
        const lngs = drivers.map((d) => d.lng);

        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);

        return L.latLngBounds([minLat, minLng], [maxLat, maxLng]);
      } catch (error) {
        return null;
      }
    },
    centerOnDriver(driver) {
      if (
        driver.lat &&
        driver.lng &&
        !isNaN(driver.lat) &&
        !isNaN(driver.lng) &&
        this.map
      ) {
        this.selectedDriver = driver;
        this.map.setView([driver.lat, driver.lng], 16, { animate: true });
      }
    },
    isSelectedDriver(driver) {
      return this.selectedDriver && this.selectedDriver.id == driver.id;
    },
    setupAbly() {
      this.ably = new Ably.Realtime({
        key: "yBmLWA.ZUTJqA:SsJ_3WOV-VWXLRGRTBf2BfIalbJS6FpggZAGlbNqJw0",
      });
      this.channel = this.ably.channels.get("driver-location");

      this.channel.subscribe("location-update", (message) => {
        this.updateDriverLocation(message.data);
      });
    },
    clearAbly() {
      if (this.channel) {
        this.channel.unsubscribe();
        this.channel = null;
      }
      if (this.ably) {
        this.ably.close();
        this.ably = null;
      }
    },
    updateDriverLocation(data) {
      const idx = this.drivers.findIndex((d) => d.id == data.id);
      const hadValidLocations = this.drivers.some(
        (driver) =>
          driver.lat && driver.lng && !isNaN(driver.lat) && !isNaN(driver.lng)
      );
      
      if (idx !== -1) {
        if (data.lat && data.lng && !isNaN(data.lat) && !isNaN(data.lng)) {
          const futureTimestamp = Date.now() + 10 * 60 * 1000;
          // Use Vue.set to ensure reactivity
          this.$set(this.drivers, idx, {
            id: data.id,
            name: this.drivers[idx].name,
            phone: this.drivers[idx].phone,
            lat: data.lat,
            lng: data.lng,
            disabled:
              !data.lat || !data.lng || isNaN(data.lat) || isNaN(data.lng),
            time: futureTimestamp,
          });

          if (
            this.isSelectedDriver(this.drivers[idx]) ||
            this.drivers[idx].lat
          ) {
            this.updateMap();

            if(!hadValidLocations) {
              this.delivery_id = data.id;
              this.centerOnDriver(this.drivers[idx]);
            }
          }
        }
      }
    },
    updateDriverStatus(data) {
      const idx = this.drivers.findIndex((d) => d.id == data.id);
      if (idx !== -1) {
        // Use Vue.set to ensure reactivity
        this.$set(this.drivers, idx, {
          ...this.drivers[idx],
          status: data.status,
          lastSeen: data.lastSeen || new Date().toISOString(),
          disabled:
            !data.lat || !data.lng || isNaN(data.lat) || isNaN(data.lng),
        });
        this.updateMap();
      }
    },
    getDrivers() {
      this.$store
        .dispatch("public__request", {
          config: {
            url: "drivers-select",
            method: "get",
          },
        })
        .then((res) => {
          this.drivers = res.data.data.map((d) => ({
            ...d,
            lat: d.lat || null,
            lng: d.lng || null,
            disabled: !d.lat || !d.lng || isNaN(d.lat) || isNaN(d.lng),
          }));
          this.initMap();
        });
    },
  },
  computed: {
    coordinates() {
      return this.$store.getters.coordinates;
    },
  },

  watch: {
    drivers: {
      handler() {
        this.updateMap();
        // Only setup Pusher bindings if not already initialized
        // This part is removed as Pusher is replaced by WebSocket
      },
      deep: true,
    },
    selectedDriver: {
      handler() {
        this.updateMap();
      },
      deep: true,
    },
  },
  mounted() {
    this.setupAbly();
    this.getDrivers();

    this.intervalId = setInterval(() => {
      const now = Date.now();

      this.drivers.forEach((driver, idx) => {
        if (driver.time && now - driver.time >= 10 * 60 * 1000) {
          this.$set(this.drivers, idx, {
            ...driver,
            lat: null,
            lng: null,
            time: null,
          });
        }
      });
    }, 60 * 1000);
  },
  beforeDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.clearAbly();
  },
};
</script>

<style scoped>
#map {
  height: 600px;
  @media (max-width: 960px) {
    height: 400px;
  }
}
.drivers-list {
  max-height: 800px;
  overflow-y: auto;
  @media (max-width: 960px) {
    max-height: 250px;
  }
}
.driver-label {
  text-align: center;
}
</style>
